import { AddCard, CreditCard } from '@mui/icons-material';
import { Box, Collapse, Fade, ToggleButton, ToggleButtonGroup } from '@mui/material';
import { CardElement, useElements, useStripe } from '@stripe/react-stripe-js';
import BackButton from '@acorn-portal/shared/src/components/buttons/BackButton';
import PaymentButton from '@acorn-portal/shared/src/components/buttons/PaymentButton';
import StripeCardPaymentForm, {
	IStripeCardPaymentFormValues,
	stripeCardFormValidationSchema,
	STRIPE_FORM_INITIAL_VALUES,
	useStripeCardPaymentFromState,
} from '@acorn-portal/shared/src/components/forms/StripeCardPaymentForm';
import ActionButtons from '@acorn-portal/shared/src/components/wrapper/action-buttons';
import ProblemModal from '@acorn-portal/shared/src/components/modal/ProblemModal';
import DebitCardSelector, {
	IExistingCardPaymentFormValues,
	EXISTING_CARD_FORM_INITIAL_VALUES,
	existingCardFormValidationSchema,
} from '@acorn-portal/shared/src/components/payment/DebitCardSelector';
import { Form, Formik } from 'formik';
import { useAppSelector } from '@acorn-portal/shared/src/hooks/reduxHook';
import {
	handleMakePaymentIntent,
	handTakeUpMidTermAdjustment,
} from '@acorn-portal/shared/src/models/midTermAdjustment-model';
import { useState } from 'react';
import { useNavigate } from 'react-router';
import { MtaPaymentCollectionOption, PaymentMethods, PaymentProvider } from '@acorn-portal/shared/src/services/api';
import { PAYMENT_ERRORS } from '@acorn-portal/shared/src/utils/constants/ErrorMessages';
import { PAYMENT_PROBLEM_MODAL } from '@acorn-portal/shared/src/utils/modalText';
import { ROUTE_PATHS } from '@acorn-portal/shared/src/utils/Routes';
import LoadingModal from '@acorn-portal/shared/src/components/modal/LoadingModal';
import { useAuth } from 'react-oidc-context';

interface IProps {
	payInFullOption: MtaPaymentCollectionOption;
	toggleModal: (open: boolean) => void;
}

const StripeCardPaymentSection = ({ payInFullOption, toggleModal }: IProps) => {
	const auth = useAuth();
	const midTermAdjustmentResponse = useAppSelector((redux) => redux.midTermAdjustmentResponse);

	const hasExistingCards = false;
	const [useExistingCard, setUseExistingCard] = useState(hasExistingCards);
	const [problem, setProblem] = useState(false);
	const [localLoading, setLocalLoading] = useState(false);
	const stripe = useStripe();
	const elements = useElements();
	const navigate = useNavigate();
	const midTermAdjustment = useAppSelector((redux) => redux.midTermAdjustment);
	const contactPreferences = useAppSelector((redux) => redux.contactPreferences);
	const { setStripeError, isCardValid } = useStripeCardPaymentFromState();
	const policyHolder = midTermAdjustment.drivers.find((x) => x.relationshipToProposer === 'P');

	const customerEmail = auth.user?.profile.email;

	const submitStripePaymentForm = async (values: IStripeCardPaymentFormValues) => {
		if (!stripe || !elements) {
			return;
		}
		const cardElement = elements.getElement(CardElement);
		if (!isCardValid) {
			if (cardElement) {
				const errorElement = document.getElementById('stripe-payment');
				if (errorElement) {
					errorElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
				}
				cardElement.focus();
				setStripeError('Invalid card details');
			}
			return;
		}
		setLocalLoading(true);
		try {
			const intentResponse = await handleMakePaymentIntent(PaymentProvider.STRIPE, midTermAdjustment, {
				paymentDetails: {
					saveCard: values.saveCard,
					amount: payInFullOption.totalAmountPayable ?? 0,
					billingAddress: values.cardholderAddress,
				},
			});

			if (!cardElement) {
				setStripeError(PAYMENT_ERRORS.preparing);
				return;
			}

			const stripeResponse = await stripe.confirmCardPayment(intentResponse.paymentId, {
				payment_method: {
					card: cardElement,
					billing_details: {
						name: `${values.cardholderFirstName} ${values.cardholderLastName}`,
						email: values.cardholderEmail,
						address: {
							line1: values.cardholderAddress.line1,
							line2: values.cardholderAddress.line2,
							postal_code: values.cardholderAddress.postcode,
							state: values.cardholderAddress.county,
							country: values.cardholderAddress.country,
						},
					},
				},
			});

			if (stripeResponse.error) {
				if (stripeResponse.error.message) {
					let msg = '';
					if (stripeResponse.error.type === 'invalid_request_error') {
						setProblem(true);
					} else {
						msg = stripeResponse.error.message;
					}
					if (msg) {
						setStripeError(msg);
					}
				}
				setLocalLoading(false);
				return;
			}

			await handTakeUpMidTermAdjustment({
				policyReference: midTermAdjustment.policyReference,
				sendPostalDocuments: contactPreferences.printToPost,
				paymentId: intentResponse.paymentId,
				authorizationId: stripeResponse.paymentIntent?.id,
				transactionId: midTermAdjustment.transactionId,
				takeUpDetails: {
					underwritingResponseId: midTermAdjustmentResponse.underwritingOutcomeResponseId,
					underwritingOptionId: midTermAdjustmentResponse.optionId,
					paymentCollectionOptionCode: payInFullOption.code,
					selectedBalanceSettlementMethod: PaymentMethods.CARD_PAYMENT,
				},
			});
			setLocalLoading(false);
			navigate(ROUTE_PATHS.makeAChangePaymentSuccess);
		} catch (error) {
			console.error('Error processing payment:', error);
			setLocalLoading(false);
			navigate(ROUTE_PATHS.error);
		}
	};

	const submitExistingCardPaymentForm = async (values: IExistingCardPaymentFormValues) => {
		try {
			setLocalLoading(true);
			await handTakeUpMidTermAdjustment({
				policyReference: midTermAdjustment.policyReference,
				sendPostalDocuments: contactPreferences.printToPost,
				existingCardPaymentMethodId: values.id,
				transactionId: midTermAdjustment.transactionId,
				takeUpDetails: {
					underwritingResponseId: midTermAdjustmentResponse.underwritingOutcomeResponseId,
					underwritingOptionId: midTermAdjustmentResponse.optionId,
					paymentCollectionOptionCode: payInFullOption.code,
					selectedBalanceSettlementMethod: PaymentMethods.CARD_PAYMENT,
				},
			});
			setLocalLoading(false);
			navigate(ROUTE_PATHS.makeAChangePaymentSuccess);
		} catch (error) {
			console.error('Error processing payment:', error);

			navigate(ROUTE_PATHS.error);
		}
	};

	if (policyHolder === undefined) return null;

	return (
		<Fade in>
			<Box>
				<LoadingModal open={localLoading} message='Processing Payment' />
				<Box sx={{ width: '100%' }}>
					<Box sx={{ margin: 2 }} />
					<Box sx={{ margin: 'auto', width: '100%', textAlign: 'center' }}>
						{hasExistingCards && (
							<ToggleButtonGroup color='primary' value={useExistingCard} exclusive aria-label='Select Card'>
								<ToggleButton
									value={false}
									onClick={() => setUseExistingCard(false)}
									sx={{ '& svg': { marginRight: '5px' } }}
									disabled={!hasExistingCards}
								>
									<AddCard />
									New Card
								</ToggleButton>
								{hasExistingCards && (
									<ToggleButton value onClick={() => setUseExistingCard(true)} sx={{ '& svg': { marginRight: '5px' } }}>
										<CreditCard />
										Existing Card
									</ToggleButton>
								)}
							</ToggleButtonGroup>
						)}
					</Box>
					<Box sx={{ margin: 3 }} />
					<Collapse
						in={!useExistingCard}
						sx={{
							'* > svg': {
								visibility: 'visible !important',
							},
						}}
					>
						<Box>
							<Formik<IStripeCardPaymentFormValues>
								initialValues={STRIPE_FORM_INITIAL_VALUES}
								onSubmit={submitStripePaymentForm}
								validationSchema={stripeCardFormValidationSchema}
							>
								{() => {
									return (
										<Form>
											<Box
												sx={{
													textAlign: 'left',
													margin: 'auto',
												}}
											>
												<StripeCardPaymentForm
													policyHolder={{
														address: midTermAdjustment.policyDetails.policyholder.address,
														firstName: policyHolder.firstName,
														lastName: policyHolder.lastNames,
														email: customerEmail ?? '',
													}}
												/>
											</Box>
											<Box sx={{ margin: 2 }} />
											<Box sx={{ paddingTop: 2 }}>
												<BackButton onClick={() => toggleModal(true)}>Back</BackButton>
												<PaymentButton type='submit' icon='Card'>
													Make Payment
												</PaymentButton>
											</Box>
										</Form>
									);
								}}
							</Formik>
						</Box>
					</Collapse>
					<Collapse
						in={useExistingCard}
						sx={{
							'* > svg': {
								visibility: 'visible !important',
							},
						}}
					>
						<Formik<IExistingCardPaymentFormValues>
							initialValues={EXISTING_CARD_FORM_INITIAL_VALUES}
							onSubmit={submitExistingCardPaymentForm}
							validationSchema={existingCardFormValidationSchema}
						>
							{() => {
								return (
									<Form>
										<Box
											sx={{
												margin: 'auto',
												maxWidth: '500px',
												textAlign: 'center',
											}}
										>
											<DebitCardSelector cards={midTermAdjustmentResponse.existingCardPaymentMethods} />
										</Box>
										<Box sx={{ margin: 4 }} />
										<ActionButtons>
											<BackButton onClick={() => toggleModal(true)}>Back</BackButton>
											<PaymentButton type='submit' icon='Card'>
												Make Payment
											</PaymentButton>
										</ActionButtons>
									</Form>
								);
							}}
						</Formik>
					</Collapse>
				</Box>
				<ProblemModal
					open={problem}
					title={PAYMENT_PROBLEM_MODAL.header}
					description={PAYMENT_PROBLEM_MODAL.text}
					onClose={() => setProblem(false)}
				/>
			</Box>
		</Fade>
	);
};

export default StripeCardPaymentSection;
